import { ActionType, createAction, getType } from "typesafe-actions";
import { vdmDashboardConstants } from "./constants";
import { jobActions } from "redux/_core/job";
import {
	ICNRForDateRangeParams,
	ICNRRecord,
	IVDMAlert,
	IVDMBunkerData,
	IVDMConsumption,
	IVDMDashboardContent,
	IVDMDashboardLoadParams,
	IVDMPICContent,
	IVDMVesselStatus,
	VDM_DASHBOARD_TABS,
} from "./@types";

export enum EVDMDashboardActions {
	VDM_VESSEL_STATUS_LOAD = "@vdmdashboard/vdm-vessel-status/load",
	VDM_BUNKER_TAB_LOAD = "@vdmdashboard/vdm-bunker-tab/load",
	VDM_CONSUMPTION_TAB_LOAD = "@vdmdashboard/vdm-consumption-tab/load",
	VDM_SMART_ALERTS_LOAD = "@vdmdashboard/vdm-smart-alerts/load",
	VDM_FUEL_CONSUMPTION_DISTANCE_COVERED_LOAD = "@vdmdashboard/vdm-fuel-consumption-distance-covered/load",
	VDM_DRAFT_SPEED_IMPACT_ON_FUEL_CONSUMPTION_LOAD = "@vdmdashboard/vdm-draft-speed-impact-on-fuel-consumption/load",
	VDM_CNR_DATE_RANGE_LOAD = "@vdmdashboard/cnr-date-range/load",
	VDM_DASHBOARD_LOAD = "@vdmdashboard/vdm-dashboard/load",
	VDM_PIC_LOAD = "@vdmdashboard/pic-content/load",
	VDM_PIC_SAVE_VESSEL_PIC = "@vdmdashboard/pic-content/save",
	VDM_PIC_UPLOAD_PIC_PICTURE = "@vdmdashboard/pic-upload-profile-picture",

	VDM_VESSEL_STATUS_SET = "@vdmdashboard/vdm-vessel-status/set",
	VDM_TAB_SET = "@vdmdashboard/vdm-tab/set",
	VDM_BUNKER_TAB_DATA_SET = "@vdmdashboard/vdm-bunker-tab-data/set",
	VDM_CONSUMPTION_TAB_DATA_SET = "@vdmdashboard/vdm-consumption-tab-data/set",
	VDM_DASHBOARD_ALERTS_TOGGLE = "@vdmdashboard/vdm-dashboard-alerts/toggle",
	VDM_SMART_ALERTS_SET = "@vdmdashboard/vdm-smart-alerts/set",
	VDM_FUEL_CONSUMPTION_DISTANCE_COVERED_SET = "@vdmdashboard/vdm-fuel-consumption-distance-covered/set",
	VDM_DRAFT_SPEED_IMPACT_ON_FUEL_CONSUMPTION_SET = "@vdmdashboard/vdm-draft-speed-impact-on-fuel-consumption/set",
	VDM_CNR_FOR_DATE_RANGE_SET = "@vdmdashboard/cnr-for-date-range/set",
	VDM_DASHBOARD_SELECTED_DATE = "@vdmdashboard/selected-date/set",
	VDM_CNRID_FOR_SELECTED_DATE = "@vdmdashboard/selected-date-cnrid/set",
	VDM_DASHBOARD_CONTENT_SET = "@vdmdashboard/dashboard-content/set",
	VDM_PIC_CONTENT_SET = "@vdmdashboard/pic-content/set"
}

const vdmDashboardActions = {
	commands: {
		vesselVDMStatusLoad: createAction(
			EVDMDashboardActions.VDM_VESSEL_STATUS_LOAD,
			(vesselId?: string) => ({ vesselId }),
		)(),
		vdmBunkerTabLoad: createAction(
			EVDMDashboardActions.VDM_BUNKER_TAB_LOAD,
			(vesselId: string) => ({ vesselId }),
		)(),
		vdmConsumptionTabLoad: createAction(
			EVDMDashboardActions.VDM_CONSUMPTION_TAB_LOAD,
			(vesselId: string) => ({ vesselId }),
		)(),
		vdmSmartAlertsLoad: createAction(
			EVDMDashboardActions.VDM_SMART_ALERTS_LOAD,
			(vesselId: string) => ({ vesselId }),
		)(),
		vdmFuelConsumptionDistanceCoveredLoad: createAction(
			EVDMDashboardActions.VDM_FUEL_CONSUMPTION_DISTANCE_COVERED_LOAD,
			(vesselId: string, duration: string) => ({
				vesselId,
				duration,
			}),
		)(),
		vdmDraftSpeedImpactOnFuelConsumptionLoad: createAction(
			EVDMDashboardActions.VDM_DRAFT_SPEED_IMPACT_ON_FUEL_CONSUMPTION_LOAD,
			(vesselId: string) => ({ vesselId }),
		)(),
		vdmCNRDateRangeLoad: createAction(
			EVDMDashboardActions.VDM_CNR_DATE_RANGE_LOAD,
			(params: ICNRForDateRangeParams) => ({ params }),
		)(),
		vdmDashboardLoad: createAction(
			EVDMDashboardActions.VDM_DASHBOARD_LOAD,
			(params: IVDMDashboardLoadParams) => ({ params }),
		)(),
		vdmPICLoad: createAction(
			EVDMDashboardActions.VDM_PIC_LOAD,
			(vesselId: string) => ({ vesselId }),
		)(),
		vdmPICSave: createAction(
			EVDMDashboardActions.VDM_PIC_SAVE_VESSEL_PIC,
			(
				vesselId: string,
				departmentWiseAssignment: IVDMPICContent["departments"],
			) => ({
				vesselId,
				departmentWiseAssignment,
			}),
		)(),
		uploadPICProfilePicture: createAction(
			EVDMDashboardActions.VDM_PIC_UPLOAD_PIC_PICTURE,
			(vesselId: string, userId: string, file: File) => ({
				vesselId,
				userId,
				file,
			}),
		)(),
	},
	document: {
		vdmVesselStatusSet: createAction(
			EVDMDashboardActions.VDM_VESSEL_STATUS_SET,
			(vdmVesselStatus: IVDMVesselStatus[]) => ({ vdmVesselStatus }),
		)(),
		vdmTabSet: createAction(
			EVDMDashboardActions.VDM_TAB_SET,
			(selectedVDMTab: VDM_DASHBOARD_TABS) => ({ selectedVDMTab }),
		)(),
		vdmBunkerTabDataSet: createAction(
			EVDMDashboardActions.VDM_BUNKER_TAB_DATA_SET,
			(bunkerData: IVDMBunkerData) => ({ bunkerData }),
		)(),
		vdmConsumptionTabDataSet: createAction(
			EVDMDashboardActions.VDM_CONSUMPTION_TAB_DATA_SET,
			(consumptionData: Omit<IVDMConsumption, "tabType">) => ({
				consumptionData,
			}),
		)(),
		vdmDashboardAlertsToggle: createAction(
			EVDMDashboardActions.VDM_DASHBOARD_ALERTS_TOGGLE,
			(isAlertsExpanded: boolean) => ({ isAlertsExpanded }),
		)(),
		vdmSmartAlertsSet: createAction(
			EVDMDashboardActions.VDM_SMART_ALERTS_SET,
			(smartAlerts: IVDMAlert[]) => ({ smartAlerts }),
		)(),
		vdmFuelConsumptionDistanceCoveredSet: createAction(
			EVDMDashboardActions.VDM_FUEL_CONSUMPTION_DISTANCE_COVERED_SET,
			(fuelConsumptionDistanceCovered: any) => ({
				fuelConsumptionDistanceCovered,
			}),
		)(),
		vdmDraftSpeedImpactOnFuelConsumptionSet: createAction(
			EVDMDashboardActions.VDM_DRAFT_SPEED_IMPACT_ON_FUEL_CONSUMPTION_SET,
			(draftSpeedImpactOnFuelConsumption: any) => ({
				draftSpeedImpactOnFuelConsumption,
			}),
		)(),
		vdmCNRForDateRangeSet: createAction(
			EVDMDashboardActions.VDM_CNR_FOR_DATE_RANGE_SET,
			(cnrForDateRange: ICNRRecord[]) => ({ cnrForDateRange }),
		)(),
		vdmDashboardSelectedDateSet: createAction(
			EVDMDashboardActions.VDM_DASHBOARD_SELECTED_DATE,
			(date: string) => ({ date }),
		)(),
		vmdDashboardAssociatedCNRIDForSelectedDate: createAction(
			EVDMDashboardActions.VDM_CNRID_FOR_SELECTED_DATE,
			(cnrId) => ({ cnrId }),
		)(),
		vdmDashboardContentSet: createAction(
			EVDMDashboardActions.VDM_DASHBOARD_CONTENT_SET,
			(dashboardContent: IVDMDashboardContent | null) => ({
				dashboardContent,
			}),
		)(),
		vdmPICContentSet: createAction(
			EVDMDashboardActions.VDM_PIC_CONTENT_SET,
			(picContent: IVDMPICContent["departments"] | null) => ({
				picContent,
			}),
		)(),
	},
};

export const vdmDashboardJobActions = {
	vesselVDMStatusLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vesselVDMStatusLoad),
	),
	vdmBunkerTabLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmBunkerTabLoad),
	),
	vdmConsumptionTabLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmConsumptionTabLoad),
	),
	vdmSmartAlertsLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmSmartAlertsLoad),
	),
	vdmFuelConsumptionDistanceCoveredLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmFuelConsumptionDistanceCoveredLoad),
	),
	vdmDraftSpeedImpactOnFuelConsumptionLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(
			vdmDashboardActions.commands.vdmDraftSpeedImpactOnFuelConsumptionLoad,
		),
	),
	vdmCNRDateRangeLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmCNRDateRangeLoad),
	),
	vdmDashboardLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmDashboardLoad),
	),
	vdmPICLoad: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmPICLoad),
	),
	vdmPICSave: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.vdmPICSave),
	),
	uploadPICProfilePicture: jobActions(
		vdmDashboardConstants.FEATURE,
		getType(vdmDashboardActions.commands.uploadPICProfilePicture),
	),
};

export type TVDMDashbaordActions = ActionType<typeof vdmDashboardActions>;
export default vdmDashboardActions;
